import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_API_URL || '';

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [firstLogin, setFirstLogin] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── Token helpers ───────────────────────────────────────────
  const getAccess  = () => localStorage.getItem('accessToken');
  const getRefresh = () => localStorage.getItem('refreshToken');

  const storeTokens = (access, refresh) => {
    localStorage.setItem('accessToken', access);
    if (refresh) localStorage.setItem('refreshToken', refresh);
  };

  const clearTokens = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  };

  // ── Refresh access token ────────────────────────────────────
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = getRefresh();
    if (!refreshToken) return null;
    try {
      const res  = await fetch(`${API}/auth/refresh`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      storeTokens(data.accessToken, data.refreshToken);
      return data.accessToken;
    } catch {
      return null;
    }
  }, []);

  // ── Restore session on page load ────────────────────────────
  useEffect(() => {
    const restore = async () => {
      let token = getAccess();
      if (!token) { setLoading(false); return; }
      try {
        let res = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          token = await refreshAccessToken();
          if (!token) { clearTokens(); setLoading(false); return; }
          res = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
          setFirstLogin(data.user.firstLogin);
        } else {
          clearTokens();
        }
      } catch {
        clearTokens();
      } finally {
        setLoading(false);
      }
    };
    restore();
  }, [refreshAccessToken]);

  // ── Login ───────────────────────────────────────────────────
  const login = async (email, password, businessId) => {
    const res  = await fetch(`${API}/auth/login`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ email, password, businessId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');
    storeTokens(data.accessToken, data.refreshToken);
    setUser(data.user);
    setFirstLogin(data.firstLogin);
    return data;
  };

  // ── Logout ──────────────────────────────────────────────────
  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${getAccess()}` },
      });
    } catch { /* best effort */ }
    clearTokens();
    setUser(null);
    setFirstLogin(false);
  };

  // ── After forced password change ────────────────────────────
  const clearFirstLogin = () => setFirstLogin(false);

  return (
    <AuthContext.Provider value={{ user, firstLogin, loading, login, logout, clearFirstLogin, getAccess, refreshAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
